import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ActivityLog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activityFilter, setActivityFilter] = useState('all');
  const [dateRange, setDateRange] = useState('7days');

  const activityTypes = [
    { value: 'all', label: 'All Activities' },
    { value: 'login', label: 'Login/Logout' },
    { value: 'user_management', label: 'User Management' },
    { value: 'permissions', label: 'Permission Changes' },
    { value: 'security', label: 'Security Events' },
    { value: 'system', label: 'System Changes' }
  ];

  const dateRangeOptions = [
    { value: '1day', label: 'Last 24 Hours' },
    { value: '7days', label: 'Last 7 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: '90days', label: 'Last 90 Days' }
  ];

  const activityLogs = [
    {
      id: 1,
      type: 'user_management',
      action: 'User Created',
      description: 'New user account created for John Smith (Chartering Desk)',
      user: 'Sarah Mitchell',
      timestamp: '2025-01-21T09:45:00Z',
      ip: '192.168.1.100',
      severity: 'info'
    },
    {
      id: 2,
      type: 'permissions',
      action: 'Permissions Modified',
      description: 'EUA Trading permissions granted to Finance Team members',
      user: 'Admin System',
      timestamp: '2025-01-21T09:30:00Z',
      ip: '192.168.1.101',
      severity: 'warning'
    },
    {
      id: 3,
      type: 'security',
      action: 'Failed Login Attempt',
      description: 'Multiple failed login attempts detected for user: mike.johnson@company.com',
      user: 'System',
      timestamp: '2025-01-21T09:15:00Z',
      ip: '203.0.113.45',
      severity: 'error'
    },
    {
      id: 4,
      type: 'login',
      action: 'User Login',
      description: 'Successful login from new device (Chrome on Windows)',
      user: 'Emma Wilson',
      timestamp: '2025-01-21T09:00:00Z',
      ip: '192.168.1.102',
      severity: 'info'
    },
    {
      id: 5,
      type: 'user_management',
      action: 'User Status Changed',
      description: 'User account suspended: david.brown@company.com (Compliance Officer)',
      user: 'Sarah Mitchell',
      timestamp: '2025-01-21T08:45:00Z',
      ip: '192.168.1.100',
      severity: 'warning'
    },
    {
      id: 6,
      type: 'system',
      action: 'Role Template Updated',
      description: 'Chartering Desk role template permissions updated',
      user: 'Admin System',
      timestamp: '2025-01-21T08:30:00Z',
      ip: '192.168.1.101',
      severity: 'info'
    },
    {
      id: 7,
      type: 'permissions',
      action: 'Bulk Permission Update',
      description: 'Applied new compliance permissions to 15 users across Operations team',
      user: 'Sarah Mitchell',
      timestamp: '2025-01-21T08:15:00Z',
      ip: '192.168.1.100',
      severity: 'info'
    },
    {
      id: 8,
      type: 'security',
      action: 'Password Reset',
      description: 'Password reset requested and completed for user: alex.thompson@company.com',
      user: 'System',
      timestamp: '2025-01-21T08:00:00Z',
      ip: '192.168.1.103',
      severity: 'info'
    }
  ];

  const filteredLogs = activityLogs?.filter(log => {
    const matchesSearch = log?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         log?.user?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         log?.action?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesType = activityFilter === 'all' || log?.type === activityFilter;
    return matchesSearch && matchesType;
  });

  const getActivityIcon = (type) => {
    const icons = {
      login: 'LogIn',
      user_management: 'Users',
      permissions: 'Shield',
      security: 'AlertTriangle',
      system: 'Settings'
    };
    return icons?.[type] || 'Activity';
  };

  const getSeverityStyles = (severity) => {
    const styles = {
      info: 'text-accent bg-accent/10',
      warning: 'text-warning bg-warning/10',
      error: 'text-error bg-error/10'
    };
    return styles?.[severity] || styles?.info;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minutes ago`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)} hours ago`;
    } else {
      return date?.toLocaleDateString() + ' ' + date?.toLocaleTimeString();
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-maritime-sm">
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Activity Log</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Monitor user activities and security events
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
            <Input
              type="search"
              placeholder="Search activities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full sm:w-64"
            />
            <Select
              options={activityTypes}
              value={activityFilter}
              onChange={setActivityFilter}
              className="w-full sm:w-48"
            />
            <Select
              options={dateRangeOptions}
              value={dateRange}
              onChange={setDateRange}
              className="w-full sm:w-48"
            />
          </div>
        </div>
      </div>
      <div className="divide-y divide-border max-h-96 overflow-y-auto">
        {filteredLogs?.map((log) => (
          <div key={log?.id} className="p-4 hover:bg-muted/30 transition-maritime">
            <div className="flex items-start space-x-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getSeverityStyles(log?.severity)}`}>
                <Icon name={getActivityIcon(log?.type)} size={16} strokeWidth={2} />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-foreground">{log?.action}</h4>
                  <span className="text-xs text-muted-foreground">{formatTimestamp(log?.timestamp)}</span>
                </div>
                
                <p className="text-sm text-muted-foreground mt-1">{log?.description}</p>
                
                <div className="flex items-center space-x-4 mt-2 text-xs text-muted-foreground">
                  <span className="flex items-center space-x-1">
                    <Icon name="User" size={12} strokeWidth={2} />
                    <span>{log?.user}</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <Icon name="Globe" size={12} strokeWidth={2} />
                    <span>{log?.ip}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredLogs?.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="Activity" size={48} strokeWidth={1} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No activities found</h3>
          <p className="text-muted-foreground">
            {searchTerm || activityFilter !== 'all' ?'Try adjusting your filters or search terms' :'Activity logs will appear here as users interact with the system'
            }
          </p>
        </div>
      )}
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Showing {filteredLogs?.length} of {activityLogs?.length} activities
          </span>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm">
              <Icon name="Download" size={14} strokeWidth={2} className="mr-2" />
              Export Log
            </Button>
            <Button variant="outline" size="sm">
              <Icon name="RefreshCw" size={14} strokeWidth={2} className="mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityLog;